import { BufferGeometry, Vector3 } from "three";

/**
 * Compute the outer boundary loops of a triangle mesh, i.e. the outline of the
 * actual analysis shape with interior holes removed.
 *
 * A boundary edge belongs to exactly one triangle. Such edges are found via a
 * parity toggle in a `Set`, which only ever retains the (few) boundary edges
 * instead of an entry per mesh edge, keeping memory bounded by the preview mesh
 * cell cap. The boundary edges are linked into loops following each owning
 * triangle's winding, so interior holes wind opposite to the outer perimeter and
 * can be dropped by comparing each loop's signed area about the plane normal.
 *
 * @param geometry The mesh geometry to outline.
 * @param planeNormal The reference plane normal used to orient the loops.
 * @returns The outer boundary loops in the geometry's local coordinate system.
 */
export function computeMeshOuterBoundaryLoops(
  geometry: BufferGeometry,
  planeNormal: Vector3,
): Vector3[][] {
  const index = geometry.getIndex();
  const position = geometry.getAttribute("position");
  const vertexCount = position.count;
  const indexCount = index ? index.count : vertexCount;

  function vertexAt(i: number): number {
    return index ? index.getX(i) : i;
  }

  // Encode an undirected edge between vertices a and b as a single number.
  function edgeKey(a: number, b: number): number {
    return a < b ? a * vertexCount + b : b * vertexCount + a;
  }

  // Boundary edges appear an odd number of times (once for a non-manifold mesh),
  // interior edges an even number. Toggling membership leaves only the boundary
  // edges in the map, which is far smaller than the full edge count.
  const boundaryEdges = new Map<number, Edge>();
  for (let i = 0; i < indexCount; i += 3) {
    const a = vertexAt(i);
    const b = vertexAt(i + 1);
    const c = vertexAt(i + 2);
    const edges = [new Edge(a, b), new Edge(b, c), new Edge(c, a)];
    for (const edge of edges) {
      const key = edgeKey(edge.a, edge.b);
      if (boundaryEdges.has(key)) {
        boundaryEdges.delete(key);
      } else {
        boundaryEdges.set(key, edge);
      }
    }
  }
  if (boundaryEdges.size === 0) return [];

  // Link boundary edges into a directed next-vertex chain, using the winding of
  // the single triangle that owns each boundary edge.
  const nextVertex = new Map<number, number>();
  for (const edge of boundaryEdges.values()) {
    nextVertex.set(edge.a, edge.b);
  }

  // Walk the chains into closed loops.
  const visited = new Set<number>();
  const loops: Vector3[][] = [];
  for (const start of nextVertex.keys()) {
    if (visited.has(start)) continue;

    const loop: Vector3[] = [];
    let current: number | undefined = start;
    while (current !== undefined && !visited.has(current)) {
      visited.add(current);
      loop.push(
        new Vector3(
          position.getX(current),
          position.getY(current),
          position.getZ(current),
        ),
      );
      current = nextVertex.get(current);
    }
    if (loop.length >= 3) loops.push(loop);
  }
  if (loops.length === 0) return [];

  // Signed area about the plane normal: outer loops share one sign, interior
  // holes the opposite. Use the largest loop to define the "outside" sign.
  const cross = new Vector3();
  const term = new Vector3();
  function signedArea(loop: Vector3[]): number {
    cross.set(0, 0, 0);
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i];
      const b = loop[(i + 1) % loop.length];
      cross.add(term.crossVectors(a, b));
    }
    return cross.dot(planeNormal);
  }

  const areas = loops.map(signedArea);
  let outerSign = 0;
  let maxArea = -Infinity;
  for (const area of areas) {
    if (Math.abs(area) > maxArea) {
      maxArea = Math.abs(area);
      outerSign = Math.sign(area);
    }
  }

  return loops.filter((_, i) => Math.sign(areas[i]) === outerSign);
}

class Edge {
  a: number;
  b: number;

  constructor(a: number, b: number) {
    this.a = a;
    this.b = b;
  }
}

/**
 * Flatten several closed loops into a single array of line-segment endpoints.
 *
 * A single closed polygon can be drawn by wrapping it in a one-element array.
 * The volume outline is the analysis selection polygon, which is already part of
 * the analysis data. Drawing it directly avoids reconstructing the boundary from
 * the volume mesh, which scales with the (potentially millions of) mesh cells
 * and previously crashed via three.js `EdgesGeometry` on large areas (SWEB-8500).
 *
 * @param loops The loops to draw, each as an ordered list of vertices.
 * @returns The combined loop edges as a flat XYZ array of segment endpoints.
 */
export function loopsToSegments(loops: Vector3[][]): Float32Array {
  let segmentCount = 0;
  for (const loop of loops) segmentCount += loop.length;

  const positions = new Float32Array(segmentCount * 6);
  let offset = 0;
  for (const loop of loops) {
    for (let i = 0; i < loop.length; i++) {
      const start = loop[i];
      const end = loop[(i + 1) % loop.length];
      positions[offset++] = start.x;
      positions[offset++] = start.y;
      positions[offset++] = start.z;
      positions[offset++] = end.x;
      positions[offset++] = end.y;
      positions[offset++] = end.z;
    }
  }
  return positions;
}
